import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Search } from "lucide-react";
import { exportXlsx } from "@/lib/exportXlsx";
import { useMe } from "@/components/app/MeContext";

const PAGE = 50;

// Register-style table: search in results, click a header to sort, 50 rows per page.
export function DataTable({ columns, rows, onRowClick, testid = "data-table", rowId = (r) => r.id, empty = "No records", toolbar, exportName: name, exportPerm, selected, onSelect, rowClickable }) {
  const { can } = useMe() || { can: () => true };
  const exportName = exportPerm && !can(exportPerm) ? null : name;
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(0);
  const list = useMemo(() => {
    let r = rows || [];
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((x) => columns.some((c) => String(c.value ? c.value(x) : x[c.key] ?? "").toLowerCase().includes(s)));
    }
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      const val = (x) => (col.value ? col.value(x) : x[col.key]);
      r = [...r].sort((a, b) => {
        const x = val(a), y = val(b);
        if (x === y) return 0;
        if (x == null || x === "") return 1;
        if (y == null || y === "") return -1;
        return (x > y ? 1 : -1) * (sort.dir === "asc" ? 1 : -1);
      });
    }
    return r;
  }, [rows, q, sort, columns]);
  const pages = Math.max(1, Math.ceil(list.length / PAGE));
  const cur = list.slice(page * PAGE, page * PAGE + PAGE);
  const toggle = (k) => setSort((s) => (s?.key === k ? (s.dir === "asc" ? { key: k, dir: "desc" } : null) : { key: k, dir: "asc" }));
  const sel = !!onSelect && !!selected;
  const allOn = sel && list.length > 0 && list.every((r) => selected.has(rowId(r)));
  const flip = (ids, on) => { const n = new Set(selected); ids.forEach((id) => (on ? n.add(id) : n.delete(id))); onSelect(n); };
  const box = "h-4 w-4 accent-sky-700 cursor-pointer";
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-slate-50/70 px-3 py-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2 top-2 h-4 w-4 text-slate-400" />
          <input className="h-8 w-full rounded border border-slate-200 bg-white pl-7 pr-2 text-xs outline-none focus:border-sky-600" placeholder="Search in results…"
            value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} data-testid={`${testid}-search`} />
        </div>
        <span className="text-xs text-slate-500" data-testid={`${testid}-count`}>{list.length} records</span>
        <div className="ml-auto flex flex-wrap gap-2">{toolbar}
          {exportName && <button onClick={() => exportXlsx(exportName, columns, list)} disabled={!list.length} data-testid={`${testid}-export`}
            className="inline-flex h-8 items-center gap-1 rounded border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"><Download className="h-3.5 w-3.5" />Export Excel</button>}
        </div>
      </div>
      <div className="max-h-[68vh] overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-slate-100">
            <tr>{sel && <th className="w-8 px-3 py-2"><input type="checkbox" className={box} checked={allOn} onChange={() => flip(list.map(rowId), !allOn)} title="Select all in results" data-testid={`${testid}-select-all`} /></th>}{columns.map((c) => (
              <th key={c.key} onClick={() => toggle(c.key)} className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-600 hover:text-slate-900 ${c.right ? "text-right" : "text-left"}`}>
                <span className="inline-flex items-center gap-1">{c.label}{sort?.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}</span>
              </th>))}
            </tr>
          </thead>
          <tbody>
            {cur.map((r, i) => (
              <tr key={rowId(r)} onClick={() => onRowClick?.(r)} data-testid={`${testid}-row-${r.order_no || r.dispatch_no || r.movement_no || r.code || rowId(r)}`}
                className={`border-b border-slate-100 ${onRowClick && (!rowClickable || rowClickable(r)) ? "cursor-pointer hover:bg-sky-50/60" : ""} ${sel && selected.has(rowId(r)) ? "bg-sky-50" : i % 2 ? "bg-slate-50/40" : ""}`}>
                {sel && <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}><input type="checkbox" className={box} checked={selected.has(rowId(r))} onChange={(e) => flip([rowId(r)], e.target.checked)} data-testid={`${testid}-select-${rowId(r)}`} /></td>}
                {columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2 text-slate-700 ${c.wrap ? "max-w-[260px] truncate" : "whitespace-nowrap"} ${c.right ? "text-right font-mono" : ""} ${c.mono ? "font-mono" : ""}`}>
                    {c.render ? c.render(r) : (r[c.key] ?? <span className="text-slate-300">—</span>)}
                  </td>
                ))}
              </tr>
            ))}
            {!cur.length && <tr><td colSpan={columns.length + (sel ? 1 : 0)} className="px-3 py-10 text-center text-sm text-slate-400" data-testid={`${testid}-empty`}>{empty}</td></tr>}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-3 py-1.5 text-xs text-slate-600">
          <button className="rounded p-1 hover:bg-slate-100 disabled:opacity-40" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></button>
          Page {page + 1} / {pages}
          <button className="rounded p-1 hover:bg-slate-100 disabled:opacity-40" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}
